/**
 * Color map for Couinaud liver segments and tumors.
 *
 * Colors are RGB tuples in [0, 1] range for VTK.js compatibility.
 */

import type { RGB } from '@/visualization/domain/types';

/**
 * Fixed color for each Couinaud segment (indexed by labelValue 1–8).
 * Distinct, colorblind-friendly palette for liver anatomy.
 */
export const SEGMENT_COLORS: Readonly<Record<number, RGB>> = {
    1: [0.84, 0, 0.42], // Segment I  — rose
    2: [0.84, 0.42, 0], // Segment II  — brown/orange
    3: [0, 0.84, 0.42], // Segment III  — green
    4: [0.84, 0.84, 0], // Segment IV  — olive/yellow
    5: [0, 0.84, 0.84], // Segment V  — teal
    6: [0.84, 0.42, 0], // Segment VI  — orange
    7: [0.84, 0, 0.42], // Segment VII  — magenta
    8: [0, 0.42, 0.84], // Segment VIII  — blue
};

/** Default color for tumors. */
export const TUMOR_COLOR: RGB = [0.98, 0.53, 0.45];

/**
 * Get the color for a Couinaud segment by labelValue.
 * Falls back to gray if the segment index is unknown.
 */
export function getSegmentColor(labelValue: number): RGB {
    return SEGMENT_COLORS[labelValue] ?? [0.6, 0.6, 0.6];
}

/** Roman numeral labels for Couinaud segments 1–8. */
export const ROMAN_NUMERALS: Readonly<Record<number, string>> = {
    1: 'I',
    2: 'II',
    3: 'III',
    4: 'IV',
    5: 'V',
    6: 'VI',
    7: 'VII',
    8: 'VIII',
};

/** Convert an RGB tuple ([0,1] range) to a CSS rgb() string. */
export function rgbToCss(rgb: RGB): string {
    return `rgb(${Math.round(rgb[0] * 255)}, ${Math.round(rgb[1] * 255)}, ${Math.round(rgb[2] * 255)})`;
}
